import AuthenticatorAppService from "../../../application/platform/security/authenticator-app-service";
import { useBaseLayoutController } from "../../base-layout/context/base-layout-controller";
import { ACTION_TYPES, useLoginDispatch, useLoginState } from "./login-context";
import { ACTION_TYPES as ACTION_TYPES_BASE_LAYOUT } from "../../base-layout/context/base-layout-context";
import { useGlobalController } from "../../global/context/global-controller";
import { useGlobalState } from "../../global/context/global-context";
import dayjs from "dayjs";
import { useBaseLayoutDispatch } from "../../base-layout/context/base-layout-context";
import { getRememberedEmail, saveRememberedEmail, clearRememberedEmail } from "../../../utils/localStorage";
import { useEffect } from "react";

export const useLoginController = () => {
    const { showMessage } = useBaseLayoutController();
    const { setAuthenticationData } = useGlobalController();
    const loginDispatch = useLoginDispatch();
    const baseLayoutDispatch = useBaseLayoutDispatch();
    const loginState = useLoginState();
    const { applicationData } = useGlobalState();

    // Initialize with remembered email on component mount
    useEffect(() => {
        const rememberedEmail = getRememberedEmail();
        if (rememberedEmail && !loginState.preAuthenticationRequest?.email) {
            loginDispatch({
                type: ACTION_TYPES.setPreAuthenticationRequest,
                payload: { 
                    type: 1, 
                    email: rememberedEmail, 
                    password: "", 
                    multiTenantId: applicationData?.multitenant 
                },
            });
            loginDispatch({
                type: ACTION_TYPES.setAuthenticationRequest,
                payload: { 
                    type: 1, 
                    email: rememberedEmail, 
                    password: "", 
                    remember: true 
                },
            });
        }
    }, [applicationData?.multitenant, loginDispatch]);
    
    const authentication = async () => {
        if (loginState.step === 1) {

            if (await validFields()) {

                baseLayoutDispatch({
                    type: ACTION_TYPES_BASE_LAYOUT.setLoading,
                    payload: true,
                });

                const data = await AuthenticatorAppService.preAuthentication(loginState.preAuthenticationRequest);

                loginDispatch({
                    type: ACTION_TYPES.setPreAuthenticationResponse,
                    payload: data,
                });

                baseLayoutDispatch({
                    type: ACTION_TYPES_BASE_LAYOUT.setLoading,
                    payload: false,
                });

                loginDispatch({
                    type: ACTION_TYPES.setStep,
                    payload: 2,
                });
            } else {

                showMessage({ type: 'error', message: 'É necessário preencher todos os campos obrigtórios.' });
            }

        } else {
            if (await validFields()) {

                baseLayoutDispatch({
                    type: ACTION_TYPES_BASE_LAYOUT.setLoading,
                    payload: true,
                });

                var result = await AuthenticatorAppService.authentication(loginState.authenticationRequest);

                // Save email if remember is checked, otherwise clear it
                if (loginState.authenticationRequest?.remember && loginState.authenticationRequest?.email) {
                    saveRememberedEmail(loginState.authenticationRequest.email);
                } else if (!loginState.authenticationRequest?.remember) {
                    clearRememberedEmail();
                }

                baseLayoutDispatch({
                    type: ACTION_TYPES_BASE_LAYOUT.setLoading,
                    payload: false,
                });

                loginDispatch({
                    type: ACTION_TYPES.setAuthenticationResponse,
                    payload: result,
                });

                loginDispatch({
                    type: ACTION_TYPES.setSucessLogin,
                    payload: true,
                });

            } else {
                showMessage({ type: 'error', message: 'É necessário preencher todos os campos obrigtórios.' });
            }
        }
    }
    const validFields = async () => {
        let isValid = true;

        if (loginState.step === 1) {
            if (loginState.preAuthenticationRequest?.type === undefined || loginState.preAuthenticationRequest?.type === 0) {
                isValid = false;
            }

            if (loginState.preAuthenticationRequest?.email === undefined || loginState.preAuthenticationRequest?.email.length === 0) {
                isValid = false;
            }
            if (loginState.preAuthenticationRequest?.password === undefined || loginState.preAuthenticationRequest?.password.length === 0) {
                isValid = false;
            }
        } else if (loginState.step === 2) {
            if (loginState.authenticationRequest?.type === undefined || loginState.authenticationRequest?.type === 0) {
                isValid = false;
            }

            if (loginState.authenticationRequest?.email === undefined || loginState.authenticationRequest?.email.length === 0) {
                isValid = false;
            }
            if (loginState.authenticationRequest?.password === undefined || loginState.authenticationRequest?.password.length === 0) {
                isValid = false;
            }

            if (loginState.authenticationRequest?.companyId === undefined || loginState.authenticationRequest?.companyId === 0) {
                isValid = false;
            }
        }

        loginDispatch({
            type: ACTION_TYPES.setFieldIsValid,
            payload: isValid,
        });

        return isValid;
    }
    const setShowPassword = async (show: boolean) => {
        loginDispatch({
            type: ACTION_TYPES.setShowPassword,
            payload: show,
        });
    }
    const resetState = async () => {
        loginDispatch({
            type: ACTION_TYPES.setStep,
            payload: 1,
        });
        loginDispatch({
            type: ACTION_TYPES.setPreAuthenticationRequest,
            payload: { type: 1, email: "", password: "", multiTenantId: applicationData?.multitenant },
        });

        loginDispatch({
            type: ACTION_TYPES.setAuthenticationRequest,
            payload: { type: 1, email: "", password: "", remember: false },
        });

        loginDispatch({
            type: ACTION_TYPES.setPreAuthenticationResponse,
            payload: {},
        });
    }

    const setInfoAuthentication = (): boolean => {
        if (loginState.authenticationResponse !== null && loginState.authenticationResponse !== undefined) {
            setAuthenticationData(loginState.authenticationResponse);
            return true;
        }
        return false;
    }

    const handleOnChange = async (e: any) => {
        let type = loginState.preAuthenticationRequest?.type;
        let user = loginState.preAuthenticationRequest?.email;
        let multiTenantId = loginState.preAuthenticationRequest?.multiTenantId;
        let password = loginState.preAuthenticationRequest?.password;
        let companyId = loginState.authenticationRequest?.companyId;
        let remember = loginState.authenticationRequest?.remember;

        if (e.target.name === "select-type") {
            type = e.target.value;

            if (e.target.value === 2 && applicationData !== undefined && applicationData.multitenant !== undefined) {
                companyId = applicationData.multitenant;

            } else if (e.target.value === 1 && applicationData !== undefined && applicationData.multitenant !== undefined) {
                multiTenantId = applicationData.multitenant;
            }
        }
        if (e.target.name === "input-user") {
            user = e.target.value;
            // If user changes email and it's different from remembered email, uncheck remember
            const rememberedEmail = getRememberedEmail();
            if (rememberedEmail && user !== rememberedEmail) {
                remember = false;
            }
        }
        if (e.target.name === "input-password") {
            password = e.target.value;
        }
        if (e.target.name === "select-company") {
            companyId = e.target.value;
        }
        if (e.target.name === "check-remember") {
            remember = e.target.checked;
        }
        loginDispatch({
            type: ACTION_TYPES.setPreAuthenticationRequest,
            payload: { email: user, password, type, multiTenantId },
        });

        loginDispatch({
            type: ACTION_TYPES.setAuthenticationRequest,
            payload: { email: user, password, type, companyId, remember },
        });
    };

    return {
        authentication, handleOnChange, resetState, setShowPassword, setInfoAuthentication
    };
}

